/**
 * Meeting notes → RAID (2026-10-01). Paste notes, AI proposes log entries
 * (each quoting the notes), the PM ticks the ones to keep. Nothing is saved
 * until "Add to the log". Backend: app/services/meeting_notes.py.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { Sparkles, X } from 'lucide-react'

import { Button } from '../ui/button'
import { meetingNotesService, type Extraction, type ProposalKind } from '../../services/meetingNotes'
import { apiErrorMessage } from '../../utils/apiError'

const KIND_LABEL: Record<ProposalKind, string> = {
  risk: 'Risk', issue: 'Issue', assumption: 'Assumption', dependency: 'Dependency', decision: 'Decision', action: 'Action',
}
const KIND_CLS: Record<ProposalKind, string> = {
  risk: 'bg-warning/15 text-warning', issue: 'bg-destructive/15 text-destructive', assumption: 'bg-muted text-foreground',
  dependency: 'bg-primary/10 text-primary', decision: 'bg-success/15 text-success', action: 'bg-primary/10 text-primary',
}

interface Props { projectId: number; onClose: () => void; onAdded: () => void }

export function MeetingNotesDialog({ projectId, onClose, onAdded }: Props) {
  const [notes, setNotes] = useState('')
  const [meetingDate, setMeetingDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [result, setResult] = useState<Extraction | null>(null)
  const [keep, setKeep] = useState<boolean[]>([])
  const [busy, setBusy] = useState(false)

  const find = async () => {
    setBusy(true)
    try {
      const res = await meetingNotesService.extract(projectId, notes, meetingDate)
      setResult(res)
      setKeep(res.items.map((i) => !i.duplicate_of))
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Couldn’t read the notes. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  const add = async () => {
    if (!result) return
    const chosen = result.items.filter((_, i) => keep[i])
    setBusy(true)
    try {
      const res = await meetingNotesService.apply(projectId, chosen, meetingDate)
      const elsewhere = (res.added.decision || 0) + (res.added.action || 0)
      toast.success(`Added ${res.total} item${res.total === 1 ? '' : 's'}` +
        (elsewhere ? ` (decisions and actions are on their own tabs)` : ''))
      onAdded()
      onClose()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Couldn’t add the items.'))
    } finally {
      setBusy(false)
    }
  }

  const chosenCount = keep.filter(Boolean).length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true"
      aria-label="Add from meeting notes">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Sparkles className="h-5 w-5 text-primary" /> Add from meeting notes
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-2 hover:bg-muted">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          {!result ? (
            <>
              <p className="text-sm text-muted-foreground">
                Paste the notes from a steering meeting, stand-up or call. AI suggests the risks, issues, assumptions,
                dependencies, decisions and actions in them — you choose what goes in the log.
              </p>
              <label className="block text-sm text-foreground">
                Meeting date
                <input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)}
                  className="mt-1 block min-h-[44px] rounded-md border border-border bg-background px-3 text-sm text-foreground" />
              </label>
              <label className="block text-sm text-foreground">
                Notes
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={12} maxLength={20000}
                  placeholder="e.g. Vendor says the API sandbox slips to 20 Oct. Omar to send the cutover plan by Friday…"
                  className="mt-1 block w-full rounded-md border border-border bg-background p-3 text-sm text-foreground" />
              </label>
            </>
          ) : (
            <>
              {result.summary && <p className="text-sm text-foreground">{result.summary}</p>}
              {result.items.length === 0 ? (
                <p className="rounded-md bg-muted p-4 text-sm text-muted-foreground">
                  Nothing in these notes needs to go in the log.
                </p>
              ) : (
                <ul className="space-y-2">
                  {result.items.map((it, i) => (
                    <li key={`${it.kind}-${it.title}`} className="rounded-md border border-border p-3">
                      <label className="flex cursor-pointer items-start gap-3">
                        <input type="checkbox" checked={keep[i] ?? false} className="mt-1 h-4 w-4"
                          onChange={(e) => setKeep((k) => k.map((v, j) => (j === i ? e.target.checked : v)))} />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className={`rounded px-2 py-0.5 text-xs font-medium ${KIND_CLS[it.kind]}`}>{KIND_LABEL[it.kind]}</span>
                            <span className="font-medium text-foreground">{it.title}</span>
                          </span>
                          <span className="mt-1 block text-xs text-muted-foreground">
                            {[it.owner && `Owner: ${it.owner}`, it.due_date && `Due ${it.due_date}`,
                              it.probability && `Likelihood ${it.probability}`, it.impact && `Impact ${it.impact}`]
                              .filter(Boolean).join(' · ')}
                          </span>
                          <span className="mt-1 block text-xs italic text-muted-foreground">“{it.quote}”</span>
                          {it.duplicate_of && (
                            <span className="mt-1 block text-xs text-warning">Looks like “{it.duplicate_of}”, already in the log.</span>
                          )}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              {result.dropped > 0 && (
                <p className="text-xs text-muted-foreground">
                  {result.dropped} suggestion{result.dropped === 1 ? ' was' : 's were'} left out because {result.dropped === 1 ? 'it' : 'they'} didn’t match anything in the notes.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          {result && <Button variant="outline" onClick={() => setResult(null)} disabled={busy}>Back to notes</Button>}
          {!result ? (
            <Button onClick={find} disabled={busy || notes.trim().length < 20}>
              {busy ? 'Reading…' : 'Find items'}
            </Button>
          ) : (
            <Button onClick={add} disabled={busy || chosenCount === 0}>
              {busy ? 'Adding…' : `Add ${chosenCount} to the log`}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
